import {Fragment} from 'react';
import {Link} from 'react-router-dom';
import {DefaultMetaTags} from '@common/seo/default-meta-tags';
import {SiteHeader} from '@common/ui/navigation/site-header';
import {Footer} from '@common/ui/footer/footer';

const sectionRows = [
  {
    title: (
      <>
        <span className="text-[#d8562a]">Distributed</span> by design
      </>
    ),
    top: `Thanks to Storj's decentralized network, every file is split into pieces and spread across thousands of nodes worldwide. No single server ever holds a complete copy of anything.`,
    bottom: `Even if multiple nodes go offline, your data stays intact and accessible.`,
    startWithCard: true,
    titleAlign: 'right',
    topImageSrc: '/images/Page_Storage/Every_File_is_Split_into_Pieces.png',
    topImageAlt: 'Every file is split into pieces',
    bottomImageSrc:
      '/images/Page_Storage/Even_if_Multiple_Nodes_go_offline.png',
    bottomImageAlt: 'Even if multiple nodes go offline',
  },
  {
    title: (
      <>
        Built for <span className="text-[#d8562a]">durability</span>
      </>
    ),
    top: `The Storj network is engineered for high durability through redundancy and continuous repair.`,
    bottom: `Your files are replicated across geographies automatically. No manual backups, no surprise data loss.`,
    startWithCard: true,
    titleAlign: 'left',
    topImageSrc: '/images/Page_Storage/High_Durability.png',
    topImageAlt: 'High durability',
    bottomImageSrc: '/images/Page_Storage/Your_Files_are_Replicated.png',
    bottomImageAlt: 'Your files are replicated',
  },
  {
    title: (
      <>
        <span className="text-[#d8562a]">Predictable</span> pricing
      </>
    ),
    top: `No egress fees buried in the fine print. No charges that balloon when you actually use your storage. You pay for what you store, nothing more.`,
    startWithCard: true,
    titleAlign: 'right',
    topImageSrc: '/images/Page_Storage/No_Egress_Fees_Burried.png',
    topImageAlt: 'No egress fees buried',
  },
];

const storageHeroImage = {
  src: '/images/Page_Storage/Built_on_storj_as_Distributed_Cloud.png',
  alt: "Built on Storj's distributed cloud",
} as const;

const storagePricingImage = {
  src: '/images/Page_Storage/Start_with_What_You_Need_v2.png',
  alt: 'Start with what you need',
} as const;

const IMAGE_FRAME_CLASS =
  'mx-auto h-[200px] w-[92%] object-contain object-center lg:h-[430px] lg:w-[90%]';

const IMAGE_WRAP_CLASS = 'w-full';

export function StoragePage() {
  return (
    <Fragment>
      <DefaultMetaTags />
      <main className="min-h-screen bg-[#f5f5f7] text-[#1a1a1a] dark:bg-[#14161a] dark:text-[#f3f4f6]">
        <SiteHeader active="features" />
        <div className="relative mx-auto w-full max-w-[1895px] px-[100px] pb-[24px] lg:pb-[25px] max-[767px]:px-[50px]">
          <section className="mx-auto mt-[56px] grid w-full max-w-[1565px] grid-cols-1 gap-[28px] lg:mt-[170px] lg:gap-[34px] lg:grid-cols-[minmax(0,1fr)_638px] lg:items-start min-[1024px]:max-[1024px]:grid-cols-[minmax(0,1fr)_400px]">
            <div>
              <h1 className="max-w-[649px] font-['Inter',sans-serif] text-[34px] font-bold leading-[1.1] text-[#1a1a1a] dark:text-[#f3f4f6] sm:text-[38px] lg:font-['JetBrains_Mono',monospace] lg:text-[60px] lg:leading-[1.08]">
                Storage that
                <br />
                scales with <span className="italic">you</span>
              </h1>
              <p className="mt-[18px] max-w-[649px] text-[18px] font-bold leading-[1.36] text-[#6a7480] dark:text-[#b3bcc8] sm:text-[20px] lg:mt-[30px] lg:text-[25px] lg:leading-[1.32]">
                Built on Storj&apos;s distributed cloud.
                <br />
                <br />
                Your files are <span className="italic text-[#d8562a]">split</span>,{' '}
                <span className="italic text-[#d8562a]">encrypted</span>, and{' '}
                <span className="italic text-[#d8562a]">spread</span> across a global
                network of nodes.
                <br />
                <br />
                <span className="italic text-[#d8562a]">No</span> single point of
                failure.
                <br />
                <span className="italic text-[#d8562a]">No</span> data centers waiting
                to go dark.
              </p>
            </div>
            <div className="flex flex-col items-center gap-[18px] lg:gap-[28px]">
              <div className={IMAGE_WRAP_CLASS}>
                <img
                  src={storageHeroImage.src}
                  alt={storageHeroImage.alt}
                  className={IMAGE_FRAME_CLASS}
                />
              </div>
              <Link
                to="/register"
                className="inline-flex h-[60px] w-[141px] items-center justify-center rounded-[24px] bg-[#2b7e20] text-[17px] font-medium text-white transition-colors hover:bg-[#3c7d44]"
              >
                Try for free
              </Link>
            </div>
          </section>

          <div className="mx-auto mt-[72px] grid w-full max-w-[1565px] grid-cols-1 gap-y-[80px] lg:mt-[110px] lg:gap-y-[132px]">
            {sectionRows.map(section => (
              <section key={section.top}>
                <h2
                  className={`font-['Inter',sans-serif] text-[32px] font-bold leading-[1.15] text-[#1a1a1a] dark:text-[#f3f4f6] sm:text-[36px] lg:text-[60px] lg:leading-[1.1] ${
                    section.titleAlign === 'right'
                      ? 'text-left lg:text-right'
                      : 'text-left'
                  }`}
                >
                  {section.title}
                </h2>
                <div className="mt-[20px] grid gap-y-[36px] lg:mt-[30px] lg:gap-y-[48px]">
                  <div className="grid grid-cols-1 items-center gap-[24px] lg:grid-cols-2 lg:gap-[36px]">
                    <div
                      className={`${
                        section.startWithCard ? 'lg:order-1' : 'lg:order-2'
                      } ${IMAGE_WRAP_CLASS}`}
                    >
                      <img
                        src={section.topImageSrc}
                        alt={section.topImageAlt}
                        className={IMAGE_FRAME_CLASS}
                      />
                    </div>
                    <p
                      className={`${
                        section.startWithCard ? 'lg:order-2' : 'lg:order-1'
                      } max-w-[631px] text-[18px] font-bold leading-[1.38] text-[#6a7480] dark:text-[#b3bcc8] sm:text-[20px] lg:text-[25px] lg:leading-[1.35]`}
                    >
                      {section.top}
                    </p>
                  </div>
                  {section.bottom && (
                    <div className="grid grid-cols-1 items-center gap-[24px] lg:grid-cols-2 lg:gap-[36px]">
                      <p
                        className={`${
                          section.startWithCard ? 'lg:order-1' : 'lg:order-2'
                        } max-w-[696px] text-[18px] font-bold leading-[1.38] text-[#6a7480] dark:text-[#b3bcc8] sm:text-[20px] lg:text-[25px] lg:leading-[1.35]`}
                      >
                        {section.bottom}
                      </p>
                      <div
                        className={`${
                          section.startWithCard ? 'lg:order-2' : 'lg:order-1'
                        } ${IMAGE_WRAP_CLASS}`}
                      >
                        {section.bottomImageSrc ? (
                          <img
                            src={section.bottomImageSrc}
                            alt={section.bottomImageAlt ?? section.topImageAlt}
                            className={IMAGE_FRAME_CLASS}
                          />
                        ) : null}
                      </div>
                    </div>
                  )}
                </div>
              </section>
            ))}

            <section>
              <h2 className="font-['Inter',sans-serif] text-[32px] font-bold leading-[1.15] text-[#1a1a1a] dark:text-[#f3f4f6] sm:text-[36px] lg:text-[60px] lg:leading-[1.1]">
                Tiers that grow with <span className="text-[#d8562a]">you</span>
              </h2>
              <div className="mt-[20px] grid grid-cols-1 items-center gap-[24px] lg:mt-[30px] lg:grid-cols-2 lg:gap-[36px]">
                <p className="max-w-[631px] text-[18px] font-bold leading-[1.38] text-[#6a7480] dark:text-[#b3bcc8] sm:text-[20px] lg:text-[25px] lg:leading-[1.35]">
                  Start with what you need today. Upgrade when you need more
                  space, unlimited shareable links/QR codes, or end-to-end
                  encryption
                </p>
                <div className={IMAGE_WRAP_CLASS}>
                  <img
                    src={storagePricingImage.src}
                    alt={storagePricingImage.alt}
                    className={IMAGE_FRAME_CLASS}
                  />
                </div>
              </div>
            </section>
          </div>

          <section className="mx-auto mt-[84px] w-full max-w-[1565px] text-center lg:mt-[130px]">
            <h2 className="font-['Inter',sans-serif] text-[36px] font-bold leading-[1.2] text-[#1a1a1a] dark:text-[#f3f4f6] sm:text-[42px] lg:text-[60px] lg:leading-[1.16]">
              Want to give it a try?
            </h2>
            <p className="mx-auto mt-[14px] max-w-[629px] text-[18px] font-normal leading-[1.4] text-[#1a1a1a] dark:text-[#e4e8ef] sm:text-[22px] lg:mt-[20px] lg:text-[30px] lg:leading-[1.35]">
              Whether you choose a free account, paid or just passing by, we have
              something for you.
            </p>
            <Link
              to="/register"
              className="mt-[22px] inline-flex h-[64px] items-center justify-center rounded-[30px] bg-[#2b7e20] px-[30px] text-[21px] font-medium text-white transition-colors hover:bg-[#3c7d44] lg:mt-[32px] lg:h-[108px] lg:rounded-[43px] lg:px-[44px] lg:text-[30px]"
            >
              Try for free
            </Link>
          </section>

          <Footer className="mt-[44px] lg:mt-[120px] lg:min-h-[190px]" />
        </div>
      </main>
    </Fragment>
  );
}
